import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { Observable, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';

import { InvalidApiVersionError } from 'app/shared/models';
import { registerApiConfig, resetApiConfig } from 'app/state/api-config/api-config.actions';
import { selectRegisterApiConfigProgress } from 'app/state/api-config/api-config.selectors';

export type SiteConnectStage = 'provide-identifier' | 'connecting' | 'error' | 'connected';

export type SiteConnectError = 'invalid-api-version' | 'connection-error';

export interface SiteConnectWizardStepState {
    stage: SiteConnectStage;
    error: SiteConnectError | undefined;
}

const initialState: SiteConnectWizardStepState = {
    stage: 'provide-identifier',
    error: undefined,
};

@Injectable()
export class SiteConnectWizardStepStore extends ComponentStore<SiteConnectWizardStepState> {
    constructor(private store: Store) {
        super(initialState);
    }

    stage$ = this.select((state) => state.stage);
    error$ = this.select((state) => state.error);

    onCodeScanned = this.effect((code$: Observable<string>) =>
        code$.pipe(
            tap((code) => {
                this.store.dispatch(registerApiConfig({ identifier: code }));
            }),
            switchMap(() => this.store.select(selectRegisterApiConfigProgress)),
            tap((progress) => {
                if (progress.running) this.updateStage('connecting');
                else if (progress.done) this.updateStage('connected');
                else if (progress.error) {
                    const error =
                        progress.errorMessage === InvalidApiVersionError
                            ? 'invalid-api-version'
                            : 'connection-error';
                    this.updateError(error);
                } else this.updateStage('provide-identifier');
            }),
        ),
    );

    private updateStage = this.updater((state, stage: SiteConnectStage) => ({ ...state, stage }));
    private updateError = this.updater((state, error: SiteConnectError) => {
        return { ...state, stage: 'error', error };
    });

    resetApiConfig = this.effect((trigger$) =>
        trigger$.pipe(tap(() => this.store.dispatch(resetApiConfig()))),
    );
}
